<template>
    <div class="cart-page">
        <!-- Header Section -->
        <div class="header-section text-center white--text">
            <h1 class="display-1 font-weight-bold mb-2">ตะกร้าสินค้า</h1>
        </div>

        <v-container class="cart-container mt-6">
            <!-- Cart expiry notice -->
            <v-alert
                v-if="cartExpiryDays > 0"
                outlined
                type="info"
                dense
                color="#F4811F"
                icon="mdi-clock-alert-outline"
                class="mb-4 cart-expiry-notice"
            >
                <span class="font-weight-bold">หมายเหตุ:</span>
                สินค้าในตะกร้าเก็บไว้ได้สูงสุด {{ cartExpiryDisplay }}
                หากเกินกำหนด ระบบจะลบรายการสินค้าในตะกร้าโดยอัตโนมัติ
            </v-alert>

            <!-- Cart Table Header (hidden on mobile) -->
            <v-card class="mb-4 pa-4 d-none d-sm-block" outlined>
                <v-row align="center" class="text-center font-weight-bold grey--text text--darken-2">
                    <v-col cols="1"></v-col>
                    <v-col cols="4" class="text-left">สินค้า</v-col>
                    <v-col cols="2">ราคา</v-col>
                    <v-col cols="2">จำนวน</v-col>
                    <v-col cols="2">รวม</v-col>
                    <v-col cols="1">จัดการ</v-col>
                </v-row>
            </v-card>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-10">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>

            <!-- Empty State -->
            <div v-else-if="groupedItems.length === 0" class="text-center py-10">
                <h3 class="grey--text">ไม่มีสินค้าในตะกร้า</h3>
            </div>

            <!-- Cart Items Grouped by Shop -->
            <div v-else>
                <CartShopGroup v-for="(group, index) in groupedItems" :key="index" :group="group"
                    :selected-ids="selectedItemIds" @toggle-select="toggleItemSelection"
                    @toggle-shop-select="toggleShopSelection" @update-qty="updateQty" @set-qty="setQty" @remove="removeItem" />
            </div>

            <!-- Bottom Summary Section -->
            <v-card class="mt-6 pa-4" outlined>
                <v-row align="center" justify="space-between">
                    <v-col cols="12" sm="6">
                        <div class="subtitle-1 font-weight-bold">ราคาสินค้ารวมทั้งหมด</div>
                        <div class="caption grey--text">สินค้าทั้งหมด {{ totalSelectedCount }}</div>
                    </v-col>
                    <v-col cols="12" sm="6" class="d-flex align-center justify-sm-end justify-space-between">
                        <div class="headline font-weight-bold primary--text">
                            {{ formatPrice(totalSelectedPrice) }} บาท
                        </div>
                        <v-btn color="primary" dark depressed class="ml-4" @click="checkout">
                            สั่งซื้อสินค้า
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card>
        </v-container>
    </div>
</template>

<script>
import { getCartApi, updateItemQuantityApi, removeItemFromCartApi } from "@/services/api/cart";
import { getPublicSystemSettingsApi } from "@/services/api/adminSettings";
import CartShopGroup from "@/components/Cart/CartShopGroup.vue";
import Swal from "sweetalert2";

export default {
    name: "Cart",
    components: {
        CartShopGroup,
    },
    data() {
        return {
            cart: null,
            selectedItems: [],
            loading: false,
            cartExpiryMinutes: 0,
        };
    },
    computed: {
        cartExpiryDays() {
            return this.cartExpiryMinutes > 0 ? this.cartExpiryMinutes / (60 * 24) : 0;
        },
        cartExpiryDisplay() {
            const m = this.cartExpiryMinutes;
            if (m <= 0) return "";
            if (m >= 60 * 24) {
                const days = m / (60 * 24);
                const rounded = Number.isInteger(days) ? days : days.toFixed(1);
                return `${rounded} วัน`;
            }
            if (m >= 60) {
                const hrs = m / 60;
                const rounded = Number.isInteger(hrs) ? hrs : hrs.toFixed(1);
                return `${rounded} ชั่วโมง`;
            }
            return `${m} นาที`;
        },
        groupedItems() {
            if (!this.cart || !this.cart.items) return [];

            const groups = {};

            this.cart.items.forEach((item) => {
                const seller = item.product?.seller;
                const shopName = seller?.shopName || "ร้านค้าทั่วไป";
                const sellerId = seller?.id || item.sellerId || "unknown";

                if (!groups[sellerId]) {
                    groups[sellerId] = {
                        sellerId,
                        shopName,
                        items: [],
                    };
                }
                groups[sellerId].items.push(item);
            });

            return Object.values(groups);
        },
        selectedItemIds() {
            return this.selectedItems.map((item) => item.id);
        },
        selectAll: {
            get() {
                if (!this.cart || !this.cart.items || this.cart.items.length === 0) return false;
                return this.selectedItems.length === this.cart.items.length;
            },
            set(val) {
                if (val) {
                    this.selectedItems = [...(this.cart.items || [])];
                } else {
                    this.selectedItems = [];
                }
            },
        },
        totalSelectedCount() {
            return this.selectedItems.length;
        },
        totalSelectedPrice() {
            return this.selectedItems.reduce((sum, item) => sum + (item.subtotal || 0), 0);
        },
    },
    mounted() {
        this.fetchCart();
        this.fetchExpirySetting();
    },
    methods: {
        async fetchExpirySetting() {
            try {
                const res = await getPublicSystemSettingsApi();
                this.cartExpiryMinutes = Number(res?.data?.cartExpiryMinutes) || 0;
            } catch (e) {
                this.cartExpiryMinutes = 0;
            }
        },
        toggleSelectAll() {
            // Handled by computed setter
        },
        toggleItemSelection(item) {
            const itemSellerId = item.product?.seller?.id || item.sellerId || "unknown";

            // Check if there are already selected items from a different seller
            const differentSeller = this.selectedItems.find(i => {
                const sId = i.product?.seller?.id || i.sellerId || "unknown";
                return sId !== itemSellerId;
            });

            if (differentSeller) {
                Swal.fire({
                    title: "เปลี่ยนร้านค้า?",
                    text: "คุณสามารถสั่งซื้อได้ทีละร้านค้าเท่านั้น ต้องการเปลี่ยนไปเลือกสินค้าจากร้านนี้แทนหรือไม่?",
                    icon: "info",
                    showCancelButton: true,
                    confirmButtonText: "ตกลง",
                    cancelButtonText: "ยกเลิก"
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.selectedItems = [item];
                    }
                });
                return;
            }

            const index = this.selectedItems.findIndex((i) => i.id === item.id);
            if (index > -1) {
                this.selectedItems.splice(index, 1);
            } else {
                this.selectedItems.push(item);
            }
        },
        toggleShopSelection({ items, selected }) {
            if (selected) {
                // If selecting a new shop, we need to check if there are selections from other shops
                const currentSellerIds = new Set(this.selectedItems.map(i => i.product?.seller?.id || i.sellerId || "unknown"));
                const targetSellerId = items[0]?.product?.seller?.id || items[0]?.sellerId || "unknown";

                if (currentSellerIds.size > 0 && !currentSellerIds.has(targetSellerId)) {
                    Swal.fire({
                        title: "เปลี่ยนร้านค้า?",
                        text: "คุณสามารถสั่งซื้อได้ทีละร้านค้าเท่านั้น ต้องการเปลี่ยนไปเลือกสินค้าจากร้านนี้แทนหรือไม่?",
                        icon: "info",
                        showCancelButton: true,
                        confirmButtonText: "ตกลง",
                        cancelButtonText: "ยกเลิก"
                    }).then((result) => {
                        if (result.isConfirmed) {
                            this.selectedItems = [...items];
                        }
                    });
                    return;
                }

                // Append items if not already selected
                items.forEach(item => {
                    if (!this.selectedItems.find(i => i.id === item.id)) {
                        this.selectedItems.push(item);
                    }
                });
            } else {
                // Deselect only items from this shop
                const itemIdsToDay = new Set(items.map(i => i.id));
                this.selectedItems = this.selectedItems.filter(i => !itemIdsToDay.has(i.id));
            }
        },
        async fetchCart() {
            this.loading = true;
            try {
                const res = await getCartApi();
                if (res && res.success) {
                    this.cart = res.data;
                    // Update selected items
                    if (this.cart.items) {
                        const currentIds = new Set(this.cart.items.map((i) => i.id));
                        this.selectedItems = this.selectedItems.filter((i) => currentIds.has(i.id));
                        this.selectedItems = this.selectedItems.map((si) => {
                            return this.cart.items.find((i) => i.id === si.id) || si;
                        });
                    }
                }
            } catch (error) {
                console.error("Error fetching cart", error);
            } finally {
                this.loading = false;
            }
        },
        formatPrice(val) {
            return Number(val).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        async setQty(item, newQty) {
            const stock = item.variant?.stock ?? item.product?.stock ?? Infinity;
            const clamped = Math.min(Math.max(parseInt(newQty) || 1, 1), stock);
            if (clamped === item.quantity) return;
            await this._applyQtyChange(item, clamped);
        },
        async updateQty(item, change) {
            const stock = item.variant?.stock ?? item.product?.stock ?? Infinity;
            const newQty = item.quantity + change;
            if (newQty < 1 || newQty > stock) return;
            await this._applyQtyChange(item, newQty);
        },
        async _applyQtyChange(item, newQty) {
            const cartItem = this.cart.items.find(i => i.id === item.id);
            if (!cartItem) return;

            const oldQty = cartItem.quantity;
            const oldSubtotal = cartItem.subtotal;
            const effectivePrice = cartItem.discountPrice > 0 ? cartItem.discountPrice : cartItem.price;

            cartItem.quantity = newQty;
            cartItem.subtotal = effectivePrice * newQty;

            const selectedItem = this.selectedItems.find(i => i.id === item.id);
            if (selectedItem) {
                selectedItem.quantity = newQty;
                selectedItem.subtotal = cartItem.subtotal;
            }

            try {
                await updateItemQuantityApi({
                    productId: item.productId,
                    quantity: newQty,
                    variantId: item.variantId,
                    options: item.options,
                });
            } catch (error) {
                cartItem.quantity = oldQty;
                cartItem.subtotal = oldSubtotal;
                if (selectedItem) {
                    selectedItem.quantity = oldQty;
                    selectedItem.subtotal = oldSubtotal;
                }
                console.error("Update qty error", error);
                Swal.fire("ข้อผิดพลาด", error.response?.data?.message || "ไม่สามารถอัปเดตจำนวนได้", "error");
            }
        },
        async removeItem(item) {
            const result = await Swal.fire({
                title: "ยืนยันการลบ",
                text: "คุณต้องการลบสินค้านี้ออกจากตะกร้าใช่หรือไม่?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "ลบสินค้า",
                cancelButtonText: "ยกเลิก",
            });

            if (result.isConfirmed) {
                try {
                    const data = {
                        productId: item.productId,
                        variantId: item.variantId,
                        options: item.options,
                    };
                    const res = await removeItemFromCartApi(data);
                    if (res.success) {
                        await this.fetchCart();
                        Swal.fire("ลบเรียบร้อย!", "สินค้าถูกลบออกจากตะกร้าแล้ว", "success");
                    }
                } catch (error) {
                    console.error("Remove item error", error);
                    Swal.fire("ข้อผิดพลาด", error.response?.data?.message || "ไม่สามารถลบสินค้าได้", "error");
                }
            }
        },
        checkout() {
            if (this.selectedItems.length === 0) {
                Swal.fire("แจ้งเตือน", "กรุณาเลือกสินค้าที่ต้องการสั่งซื้อ", "warning");
                return;
            }

            // Backend validation also exists, but let's be safe on frontend
            const sellerIds = [...new Set(this.selectedItems.map(i => i.product?.seller?.id || i.sellerId || "unknown"))];
            if (sellerIds.length > 1) {
                Swal.fire("แจ้งเตือน", "กรุณาสั่งซื้อสินค้าทีละร้านค้า", "warning");
                return;
            }

            // Prepare order data
            const orderData = {
                items: this.selectedItems,
                shippingFee: 0, // Will be calculated based on shipping method
            };

            // Store in localStorage for Order page to access
            localStorage.setItem('orderData', JSON.stringify(orderData));

            // Navigate to Order page
            this.$router.push({ name: "order" });
        },
    },
};
</script>

<style scoped>
.cart-page {
    background-color: #f5f5f5;
    min-height: 100vh;
}

.header-section {
    background: linear-gradient(90deg, #F4811F 0%, #FFF5E6 100%);
    padding: 40px 0;
}

@media (max-width: 600px) {
    .header-section {
        padding: 24px 0;
    }

    .header-section h1 {
        font-size: 1.5rem !important;
    }
}

.cart-container {
    max-width: 1440px;
    margin: 0 auto;
    padding: 0 40px;
    box-sizing: border-box;
}

@media (min-width: 1440px) and (max-width: 1919px) {
    .cart-container {
        max-width: 1200px;
    }
}

@media (max-width: 768px) {
    .cart-container {
        padding: 0 16px;
    }
}
</style>
